import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "@/components/Modal";
import {
  DEFAULT_SHORTCUT_BINDINGS,
  SHORTCUT_CATALOG,
  conflictingShortcutCommand,
  persistKeyboardShortcutSettings,
  shortcutKeyLabels,
  useKeyboardShortcutSettings,
  type ShortcutBinding,
  type ShortcutCommand,
  type ShortcutSection,
} from "@/lib/keyboardShortcuts";

const SECTIONS: ShortcutSection[] = ["General", "Create", "Chats", "Tasks"];

/** Said plainly rather than hidden: browsers keep ⌘W for their own tab and never
 * hand it to the page, so the row above is honest only with this next to it. */
const SECTION_HINTS: Partial<Record<ShortcutSection, string>> = {
  Chats: "Your browser may keep ⌘W for its own tab — customise Close chat tab if it never reaches Rivets.",
};

export function KeyboardShortcutsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [selectedCommand, setSelectedCommand] = useState<ShortcutCommand>(SHORTCUT_CATALOG[0]!.command);
  const [customizingCommand, setCustomizingCommand] = useState<ShortcutCommand>();
  const bindings = useKeyboardShortcutSettings((state) => state.bindings);
  const groups = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return SECTIONS.map((section) => ({
      section,
      items: SHORTCUT_CATALOG.filter((item) => item.section === section && (!normalized || item.label.toLowerCase().includes(normalized))),
    })).filter((group) => group.items.length > 0);
  }, [query]);
  const visibleCommands = useMemo(() => groups.flatMap((group) => group.items.map((item) => item.command)), [groups]);

  useEffect(() => {
    if (!open) return;
    setSelectedCommand((current) => visibleCommands.includes(current) ? current : (visibleCommands[0] ?? SHORTCUT_CATALOG[0]!.command));
  }, [open, visibleCommands]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const moveSelection = (offset: number) => {
    if (visibleCommands.length === 0) return;
    const currentIndex = visibleCommands.indexOf(selectedCommand);
    const nextIndex = currentIndex < 0
      ? 0
      : Math.max(0, Math.min(visibleCommands.length - 1, currentIndex + offset));
    const command = visibleCommands[nextIndex]!;
    setSelectedCommand(command);
    requestAnimationFrame(() => document.querySelector(`[data-shortcut-command="${command}"]`)?.scrollIntoView({ block: "nearest" }));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveSelection(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter" && visibleCommands.includes(selectedCommand)) {
      event.preventDefault();
      setCustomizingCommand(selectedCommand);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title="Keyboard shortcuts" width={760}>
      <div className="shortcut-reference" onKeyDown={handleKeyDown}>
        <label className="shortcut-search">
          <span aria-hidden="true">⌕</span>
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search shortcuts…"
            aria-label="Search shortcuts"
          />
        </label>
        <div className="shortcut-reference-scroll" role="listbox" aria-label="Keyboard shortcuts">
          {groups.map(({ section, items }) => (
            <section className="shortcut-section" key={section}>
              <h3>{section}</h3>
              <div className="shortcut-list">
                {items.map((item) => (
                  <div
                    className={`shortcut-row${selectedCommand === item.command ? " selected" : ""}`}
                    key={item.command}
                    data-shortcut-command={item.command}
                    role="option"
                    aria-selected={selectedCommand === item.command}
                    onMouseEnter={() => setSelectedCommand(item.command)}
                    onClick={() => setSelectedCommand(item.command)}
                  >
                    <span>{item.label}</span>
                    <span className="shortcut-row-actions">
                      <button
                        type="button"
                        className="shortcut-customize-button"
                        onClick={(event) => { event.stopPropagation(); setCustomizingCommand(item.command); }}
                      >
                        Customise
                      </button>
                      <ShortcutKeycaps binding={bindings[item.command]} />
                    </span>
                  </div>
                ))}
              </div>
              {SECTION_HINTS[section] ? <p className="shortcut-section-hint">{SECTION_HINTS[section]}</p> : null}
            </section>
          ))}
          {groups.length === 0 ? <div className="shortcut-empty">No shortcuts match “{query}”.</div> : null}
        </div>
      </div>
      </Modal>
      <ShortcutCaptureModal command={customizingCommand} onClose={() => setCustomizingCommand(undefined)} />
    </>
  );
}

function ShortcutKeycaps({ binding, large = false }: { binding: ShortcutBinding; large?: boolean }) {
  const labels = shortcutKeyLabels(binding);
  return (
    <span className={`shortcut-key-group${large ? " large" : ""}`} aria-label={labels.join(" plus ")}>
      {labels.map((key, index) => <kbd key={`${key}:${index}`}>{key}</kbd>)}
    </span>
  );
}

function ShortcutCaptureModal({ command, onClose }: { command?: ShortcutCommand; onClose: () => void }) {
  const item = SHORTCUT_CATALOG.find((candidate) => candidate.command === command);
  const bindings = useKeyboardShortcutSettings((state) => state.bindings);
  const setBinding = useKeyboardShortcutSettings((state) => state.setBinding);
  const setCapturing = useKeyboardShortcutSettings((state) => state.setCapturing);
  const [candidate, setCandidate] = useState<ShortcutBinding>();
  const [pressedLabels, setPressedLabels] = useState<string[]>([]);
  const [message, setMessage] = useState("Hold Command or Control, then press another key");
  const candidateRef = useRef<ShortcutBinding | undefined>(undefined);
  const pendingRef = useRef<ShortcutBinding | undefined>(undefined);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!command) return;
    const current = bindings[command];
    candidateRef.current = current;
    pendingRef.current = undefined;
    setCandidate(current);
    setPressedLabels(shortcutKeyLabels(current));
    setMessage("Press your new shortcut");
    setCapturing(true);
    stageRef.current?.focus();

    return () => setCapturing(false);
  }, [bindings, command, setCapturing]);

  useEffect(() => {
    if (!command) return;
    const saveCandidate = () => {
      const binding = candidateRef.current;
      if (!binding || conflictingShortcutCommand(command, binding, bindings)) return;
      setBinding(command, binding);
      void persistKeyboardShortcutSettings().catch(() => { /* The dirty offline cache retries on bootstrap. */ });
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.key === "Escape") {
        onClose();
        return;
      }

      const hasChordModifier = event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
      if (event.key === "Enter" && !hasChordModifier) {
        saveCandidate();
        return;
      }

      const modifierLabels = [
        ...(event.metaKey ? ["⌘"] : []),
        ...(event.altKey ? ["⌥"] : []),
        ...(event.shiftKey ? ["⇧"] : []),
        ...(event.ctrlKey ? ["⌃"] : []),
      ];
      const isModifier = ["Meta", "Control", "Alt", "Shift"].includes(event.key);
      setPressedLabels(isModifier ? modifierLabels : [...modifierLabels, event.key.length === 1 ? event.key.toUpperCase() : event.key]);
      if (isModifier) {
        pendingRef.current = undefined;
        setMessage("Now press another key");
        return;
      }
      if (!(event.metaKey || event.ctrlKey || event.altKey)) {
        pendingRef.current = undefined;
        setMessage("Include Command, Control, or Option so typing stays uninterrupted");
        return;
      }
      if (event.key.length !== 1) {
        pendingRef.current = undefined;
        setMessage("Use a letter, number, or punctuation key");
        return;
      }
      pendingRef.current = {
        key: event.key.toLowerCase(),
        modifiers: [
          ...(event.metaKey ? ["command" as const] : []),
          ...(event.altKey ? ["option" as const] : []),
          ...(event.shiftKey ? ["shift" as const] : []),
          ...(event.ctrlKey ? ["control" as const] : []),
        ],
      };
      setMessage("Release to confirm");
    };
    const onKeyUp = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const isModifier = ["Meta", "Control", "Alt", "Shift"].includes(event.key);
      if (isModifier) {
        if (!pendingRef.current && candidateRef.current) {
          setPressedLabels(shortcutKeyLabels(candidateRef.current));
        }
        return;
      }
      const pending = pendingRef.current;
      if (!pending || event.key.toLowerCase() !== pending.key.toLowerCase()) return;
      pendingRef.current = undefined;
      candidateRef.current = pending;
      setCandidate(pending);
      setPressedLabels(shortcutKeyLabels(pending));
      setMessage("Press Enter to save");
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup", onKeyUp, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("keyup", onKeyUp, { capture: true });
    };
  }, [bindings, command, onClose, setBinding]);

  if (!command || !item) return null;
  const conflictCommand = candidate ? conflictingShortcutCommand(command, candidate, bindings) : undefined;
  const conflict = SHORTCUT_CATALOG.find((catalogItem) => catalogItem.command === conflictCommand);

  return (
    <Modal open onClose={onClose} title={`Customise ${item.label}`} width={680}>
      <div className="shortcut-capture">
        <div className="shortcut-capture-stage" ref={stageRef} tabIndex={-1}>
          <span className="shortcut-capture-eyebrow">Press your shortcut</span>
          {pressedLabels.length ? (
            <span className="shortcut-key-group large" aria-label={pressedLabels.join(" plus ")}>
              {pressedLabels.map((key, index) => <kbd key={`${key}:${index}`}>{key}</kbd>)}
            </span>
          ) : <span className="shortcut-capture-placeholder">Waiting for keys…</span>}
          <span className={`shortcut-capture-message${conflict ? " error" : ""}`}>
            {conflict ? `Already used by “${conflict.label}”` : message}
          </span>
        </div>
        <div className="shortcut-capture-footer">
          <button className="btn btn-ghost" type="button" onClick={() => {
            const binding = DEFAULT_SHORTCUT_BINDINGS[command];
            candidateRef.current = binding;
            pendingRef.current = undefined;
            setCandidate(binding);
            setPressedLabels(shortcutKeyLabels(binding));
            setMessage("Press Enter to save");
          }}>Restore default</button>
          <span className="shortcut-capture-footer-actions">
            <button className="btn btn-ghost" type="button" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" type="button" disabled={!candidate || Boolean(conflict)} onClick={() => {
              if (!candidate || conflict) return;
              setBinding(command, candidate);
              void persistKeyboardShortcutSettings().catch(() => { /* The dirty offline cache retries on bootstrap. */ });
              onClose();
            }}>Save shortcut</button>
          </span>
        </div>
      </div>
    </Modal>
  );
}
